import React from 'react';
import { Box, FormControl, FormLabel, Radio, RadioGroup } from '@mui/material';
import { Uniform } from '../../../../components/uniform/uniform';
import { UniformStepContainer } from '../../../../components/uniform/uniform-step-container';
import { UniformStep } from '../../../../components/uniform/uniform-step';
import { EducatorTitlezone } from './educator-titlezone';
import { EducatorLaunchbox } from './educator-launchbox';
import { Accordion, AccordionSummary, AccordionDetails } from '../../../../components/accordion';
import { Row } from '../../../../components/row';
import { Cell } from '../../../../components/cell';
import { EducatorState, EducatorDtoErrors } from '../../interfaces';
import { TextField } from '../../../../components/text-field';
import { CompanyLookup, UserLookup } from '../../../../components/lookups';
import { RadioOption } from '../../../../components/radio-group';
import { YesNo } from '../../../../components/radio-group/yes-no-component';
import { ICompanyLookup } from '../../../../components/lookups/company-lookup/interfaces';
import { formatDateTime, toDate } from '../../../../utilities';
import { ReadonlyInput } from '../../../../components/readonly-input';

interface IEducatorComponentProps {
  isEditmode: boolean;
  isReadOnly: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  isGeneratingActivationKey: boolean;
  isRevokingActivationKey: boolean;
  educator: EducatorState;
  onSaveEducatorClick: () => void;
  onValidate: () => boolean;
  onStringChange: <K extends keyof EducatorState>(name: K, value?: string) => void;
  onCaseworkerChange: (id?: string) => void;
  assignUser: boolean;
  onAssignUserChange: (value: boolean) => void;
  onCompanyChange: (value: ICompanyLookup) => void;
  errors: EducatorDtoErrors;
  onDeleteClick: () => void;
  onGenerateActivationKeyClick: () => void;
  onRevokeActivationKeyClick: () => void;
}

export function EducatorComponent(props: IEducatorComponentProps) {
  const {
    isEditmode,
    isReadOnly,
    onSaveEducatorClick,
    isSaving,
    isDeleting,
    isGeneratingActivationKey,
    isRevokingActivationKey,
    onValidate,
    educator,
    onStringChange,
    onAssignUserChange,
    onCompanyChange,
    onDeleteClick,
    onGenerateActivationKeyClick,
    onRevokeActivationKeyClick,
    assignUser,
    errors,
    onCaseworkerChange,
  } = props;

  const { caseworkerId, email, name, phone, companyId, isDeviceEnrolled, activationKeyGeneratedOn, activationKeyGeneratedBy, activationKeyExpiresAt, deviceEnrolledOn } = educator;

  function handleYesNoChange(event: React.ChangeEvent<HTMLInputElement>) {
    const newValue = (event.target as HTMLInputElement).value as YesNo;
    onAssignUserChange(newValue === YesNo.Yes);
  }

  function describeActivationKeyStatus() {
    if (isDeviceEnrolled) {
      return `Enhed tilknyttet ${formatDateTime(deviceEnrolledOn)}`;
    }
    const expiresAt = toDate(activationKeyExpiresAt);
    if (!expiresAt) {
      return undefined;
    }
    return expiresAt < new Date() ? `Udløbet ${formatDateTime(expiresAt)}` : `Gyldig til ${formatDateTime(expiresAt)}`;
  }

  return (
    <Uniform
      titleZone={<EducatorTitlezone isEditMode={isEditmode} />}
      launchbox={
        <EducatorLaunchbox
          isReadOnly={isReadOnly}
          isSaving={isSaving}
          isDeleting={isDeleting}
          isGeneratingActivationKey={isGeneratingActivationKey}
          isRevokingActivationKey={isRevokingActivationKey}
          onSave={onSaveEducatorClick}
          onValidate={onValidate}
          onDelete={onDeleteClick}
          onGenerateActivationKey={onGenerateActivationKeyClick}
          canRevokeActivationKey={educator.isDeviceEnrolled || educator.hasPendingActivationKey}
          onRevokeActivationKey={onRevokeActivationKeyClick}
        />
      }
      isEditMode={isEditmode}
      showButtons={!isEditmode}
      onOk={onSaveEducatorClick}
      onCancel={() => window.close()}
      okButtonIsLoading={isSaving}
      showSpinnerOnNextAndComplete={isSaving}
    >
      <UniformStep stepName="Generelt" validate={onValidate}>
        <UniformStepContainer>
          <Accordion defaultExpanded>
            <AccordionSummary>Underviser</AccordionSummary>
            <AccordionDetails>
              <Box mt={1}>
                <Row>
                  <Cell size="full">
                    <CompanyLookup label="Virksomhed" value={companyId} onChange={onCompanyChange} error={!!errors.companyId} errorText={errors.companyId} required readOnly={isReadOnly} />
                  </Cell>
                </Row>
                <Row>
                  <Cell size="full">
                    {!isReadOnly ? (
                      <FormControl component="fieldset" sx={{ paddingLeft: 1.7 }}>
                        <FormLabel id="active-radios" component="legend">
                          Tilknyt fasit bruger
                        </FormLabel>
                        <RadioGroup aria-labelledby="active-radios" row name="assignUser" value={assignUser ? YesNo.Yes : YesNo.No} onChange={handleYesNoChange}>
                          <RadioOption value={YesNo.Yes} control={<Radio />} label="Ja" />
                          <RadioOption value={YesNo.No} control={<Radio />} label="Nej" />
                        </RadioGroup>
                      </FormControl>
                    ) : (
                      <ReadonlyInput label="Tilknyt fasit bruger" value={assignUser ? 'Ja' : 'Nej'} />
                    )}
                  </Cell>
                </Row>
                {!assignUser && (
                  <>
                    <Row>
                      <Cell size="full">
                        <FormControl component="fieldset">
                          <FormLabel component="legend" sx={{ fontWeight: 600 }}>Ekstern underviser</FormLabel>
                        </FormControl>
                      </Cell>
                    </Row>
                    <Row>
                      <Cell size="full">
                        {!isReadOnly ? (
                          <TextField
                            variant="outlined"
                            value={name}
                            label="Navn"
                            onChange={event => onStringChange('name', event.target.value)}
                            required
                            error={!!errors.name}
                            errorText={errors.name}
                          />
                        ) : (
                          <ReadonlyInput label="Navn" value={name} />
                        )}
                      </Cell>
                    </Row>
                    <Row>
                      <Cell size="half">
                        {!isReadOnly ? (
                          <TextField variant="outlined" value={phone} label="Telefon" onChange={event => onStringChange('phone', event.target.value)} />
                        ) : (
                          <ReadonlyInput label="Telefon" value={phone} />
                        )}
                      </Cell>
                      <Cell size="half">
                        {!isReadOnly ? (
                          <TextField variant="outlined" value={email} label="Email" onChange={event => onStringChange('email', event.target.value)} />
                        ) : (
                          <ReadonlyInput label="Email" value={email} />
                        )}
                      </Cell>
                    </Row>
                  </>
                )}
                {assignUser && (
                  <Row>
                    <Cell size="full">
                      <UserLookup
                        value={caseworkerId}
                        onChange={(value?: string) => onCaseworkerChange(value)}
                        required
                        readOnly={isReadOnly}
                        error={!!errors.caseworkerId}
                        errorText={errors.caseworkerId}
                      />
                    </Cell>
                  </Row>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
          {activationKeyGeneratedOn && (
            <Accordion defaultExpanded>
              <AccordionSummary>Aktiveringsnøgle</AccordionSummary>
              <AccordionDetails>
                <Box mt={1}>
                  <Row>
                    <Cell size="thirds">
                      <ReadonlyInput label="Genereret" value={formatDateTime(activationKeyGeneratedOn)} />
                    </Cell>
                    <Cell size="thirds">
                      <UserLookup value={activationKeyGeneratedBy} onChange={() => { }} label="Genereret af" readOnly />
                    </Cell>
                    <Cell size="thirds">
                      <ReadonlyInput label="Status" value={describeActivationKeyStatus()} />
                    </Cell>
                  </Row>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
        </UniformStepContainer>
      </UniformStep>
    </Uniform>
  );
}
